import React, { useEffect, useState, useCallback } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClockWidget, timeOf, statusStyle } from "@/components/ClockWidget";

export default function Attendance() {
  const { hasPerm } = useAuth();
  const isManager = hasPerm("dashboard.manager");
  const isHr = hasPerm("employee.edit");
  const [history, setHistory] = useState([]);
  const [team, setTeam] = useState([]);
  const [flags, setFlags] = useState([]);

  const load = useCallback(() => {
    api.get("/attendance/me").then(({ data }) => setHistory(data)).catch(() => {});
    if (isManager) api.get("/attendance/team").then(({ data }) => setTeam(data)).catch(() => {});
    if (isHr) api.get("/attendance/face-flags").then(({ data }) => setFlags(data)).catch(() => {});
  }, [isManager, isHr]);
  useEffect(() => { load(); }, [load]);

  const resolveFlag = async (id) => { try { await api.post(`/attendance/face-flags/${id}/resolve`); setFlags((f) => f.filter((x) => x.id !== id)); } catch { /* ignore */ } };

  const presentCount = team.filter((t) => t.status && t.status !== "Absent").length;

  return (
    <div className="uemp-fade-in">
      <PageHeader title="Clock In/Out" subtitle="Face-verified, geofenced clock in/out with schedule-based lateness." />

      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <div className="lg:col-span-1"><ClockWidget onClocked={load} /></div>
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-sm font-medium text-slate-800 mb-3">Recent Attendance</div>
          <div className="space-y-2 max-h-[260px] overflow-y-auto" data-testid="attendance-history">
            {history.slice(0, 12).map((h) => (
              <div key={h.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2">
                <span className="text-slate-700 w-24">{fmtDate(h.date)}</span>
                <span className="text-xs text-slate-500 font-mono flex-1">{timeOf(h.clock_in)} → {timeOf(h.clock_out)}</span>
                {h.site_name && <span className="text-[11px] text-slate-400 hidden sm:inline mr-2">{h.site_name}</span>}
                <Badge className={`${statusStyle[h.status] || "bg-slate-100 text-slate-600"} hover:opacity-100 text-[10px]`}>{h.status}{h.late_minutes ? ` ${h.late_minutes}m` : ""}</Badge>
              </div>
            ))}
            {history.length === 0 && <div className="text-sm text-slate-400">No records yet.</div>}
          </div>
        </div>
      </div>

      {isHr && flags.length > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 overflow-hidden mb-6" data-testid="face-review-panel">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
            <span className="text-sm font-medium text-amber-800">Face verification review</span>
            <Badge className="bg-amber-200 text-amber-900 text-[10px]">{flags.length}</Badge>
          </div>
          <div className="divide-y divide-slate-100">
            {flags.map((f) => (
              <div key={f.id} className="flex items-center gap-3 px-5 py-3" data-testid="face-flag-row">
                {f.photo ? <img src={f.photo} alt="" className="h-11 w-11 rounded-lg object-cover border border-slate-200" /> : <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-xs">N/A</div>}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800">{f.employee_name}</div>
                  <div className="text-xs text-slate-500">{fmtDate(f.date)} · {f.action.replace("_", " ")} · {f.reason}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => resolveFlag(f.id)} data-testid={`resolve-flag-${f.id}`}>Resolve</Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isManager && (
        <Tabs defaultValue="team">
          <TabsList><TabsTrigger value="team" data-testid="attendance-tab-team">Team — Today ({presentCount}/{team.length} present)</TabsTrigger></TabsList>
          <TabsContent value="team">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-slate-50"><TableHead>Employee</TableHead><TableHead>Site</TableHead><TableHead>Clock In</TableHead><TableHead>Clock Out</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody data-testid="team-attendance-table">
                  {team.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No direct reports.</TableCell></TableRow>}
                  {team.map((t) => (
                    <TableRow key={t.employee_id}>
                      <TableCell className="text-sm font-medium text-slate-800">{t.name}</TableCell>
                      <TableCell className="text-sm text-slate-600">{t.site_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{timeOf(t.clock_in)}</TableCell>
                      <TableCell className="font-mono text-xs">{timeOf(t.clock_out)}</TableCell>
                      <TableCell><Badge className={`text-[10px] ${statusStyle[t.status] || "bg-red-50 text-red-700"} hover:opacity-100`}>{t.status}{t.late_minutes ? ` · ${t.late_minutes}m` : ""}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
